<template>
  <div class="kiosk-shell" :class="{ 'kiosk-shell--home': isHome }">
    <div class="shell-orb shell-orb--one"></div>
    <div class="shell-orb shell-orb--two"></div>

    <header class="kiosk-header">
      <button
        class="brand-lockup"
        type="button"
        aria-label="Bohm Technologies home"
        @click="hiddenMenu"
      >
        <span class="brand-mark"><img src="/img/logo.svg" alt=""/></span>
        <span class="brand-copy">
          <span class="brand-name">Bohm</span>
          <span class="brand-company">technologies</span>
        </span>
      </button>

      <div class="header-actions">
        <div
          class="connection-status"
          :class="`connection-status--${connectionStatus}`"
          :title="connectionLabel"
        >
          <span class="connection-dot"></span>
          <span>{{ connectionLabel }}</span>
        </div>
        <div class="clock-status">
          <IconGlyph name="clock" :size="16" />
          <span>{{ currentTime }}</span>
        </div>
      </div>
    </header>

    <main class="kiosk-main" :class="{ 'kiosk-main--home': isHome }">
      <div v-if="!isHome" class="page-crumb">
        <button type="button" @click="$router.push('/')">
          <IconGlyph name="arrow-left" :size="18" />
          <span>Home</span>
        </button>
        <span class="crumb-slash">/</span>
        <span>{{ title || $route.name }}</span>
      </div>
      <div class="kiosk-content" :class="{ 'kiosk-content--home': isHome }">
        <slot name="body"></slot>
      </div>
    </main>

    <footer v-if="!hideFooter" class="kiosk-footer">
      <div class="footer-help">
        <span class="footer-help-icon"
          ><IconGlyph name="phone" :size="17"
        /></span>
        <span>Need a hand?</span>
        <strong>(972) 646-2550</strong>
      </div>
      <span class="footer-note"
        >Please ask a Bohm team member if you need assistance.</span
      >
    </footer>
  </div>
</template>

<script>
import IconGlyph from "@/components/IconGlyph";

export default {
  name: "MainContainer",
  components: { IconGlyph },
  props: {
    isHome: Boolean,
    hideLogo: Boolean,
    hideFooter: Boolean,
    title: String
  },
  data() {
    return {
      currentTime: "",
      clockInterval: null,
      connectionStatus: "checking",
      connectionInterval: null,
      onlineHandler: null,
      offlineHandler: null
    };
  },
  mounted() {
    this.updateTime();
    this.clockInterval = setInterval(() => this.updateTime(), 30000);
    this.onlineHandler = () => {
      this.connectionStatus = "online";
      this.checkConnection();
    };
    this.offlineHandler = () => {
      this.connectionStatus = "offline";
    };
    window.addEventListener("online", this.onlineHandler);
    window.addEventListener("offline", this.offlineHandler);
    this.checkConnection();
    this.connectionInterval = setInterval(() => this.checkConnection(), 30000);
  },
  beforeDestroy() {
    clearInterval(this.clockInterval);
    clearInterval(this.connectionInterval);
    window.removeEventListener("online", this.onlineHandler);
    window.removeEventListener("offline", this.offlineHandler);
  },
  computed: {
    connectionLabel() {
      if (this.connectionStatus === "checking") return "Checking connection";
      return this.connectionStatus === "online" ? "Online" : "Offline";
    }
  },
  methods: {
    updateTime() {
      this.currentTime = new Intl.DateTimeFormat([], {
        hour: "numeric",
        minute: "2-digit"
      }).format(new Date());
    },
    async checkConnection() {
      if (!navigator.onLine) {
        this.connectionStatus = "offline";
        return;
      }

      try {
        await fetch("https://boss-audit-api.bohmtech.com", {
          method: "HEAD",
          mode: "no-cors",
          cache: "no-store"
        });
        this.connectionStatus = "online";
      } catch (error) {
        this.connectionStatus = "offline";
      }
    },
    hiddenMenu() {
      if (this.$route.name === "Home") {
        this.$router.push("/admin").catch(() => {});
      } else {
        this.$router.push("/");
      }
    }
  }
};
</script>

<style>
.kiosk-shell {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  overflow: hidden;
  background: var(--paper);
}

.shell-orb {
  position: absolute;
  z-index: 0;
  pointer-events: none;
  border-radius: 999px;
  filter: blur(1px);
  opacity: 0.85;
}

.shell-orb--one {
  top: -17vw;
  right: -8vw;
  width: 45vw;
  height: 45vw;
  background: #eeeee8;
}

.shell-orb--two {
  bottom: -23vw;
  left: -12vw;
  width: 42vw;
  height: 42vw;
  background: #f8e6d2;
}

.kiosk-header,
.kiosk-main,
.kiosk-footer {
  position: relative;
  z-index: 1;
}

.kiosk-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: calc(100% - 96px);
  min-height: 82px;
  margin: 0 auto;
  border-bottom: 1px solid rgba(30, 41, 42, 0.1);
}

.brand-lockup,
.page-crumb button {
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
}

.brand-lockup {
  display: inline-flex;
  align-items: center;
  padding: 0;
  color: var(--ink);
  text-align: left;
}

.brand-mark {
  display: grid;
  width: 38px;
  height: 46px;
  margin-right: 11px;
  place-items: center;
  overflow: hidden;
}

.brand-mark img {
  width: 40px;
  height: 48px;
  object-fit: contain;
}

.brand-copy {
  display: flex;
  flex-direction: column;
  line-height: 1;
}

.brand-name {
  font-family: Eurosoft, -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
  font-size: 26px;
  letter-spacing: 0.015em;
}

.brand-company {
  margin-top: 5px;
  color: var(--muted);
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.23em;
  text-transform: uppercase;
}

.header-actions,
.connection-status,
.clock-status,
.page-crumb button {
  display: flex;
  align-items: center;
}

.header-actions {
  gap: 20px;
}

.connection-status {
  gap: 8px;
  color: var(--muted);
  font-size: 12px;
  font-weight: 800;
}

.connection-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #aab1ad;
  animation: connection-flash 1.4s ease-in-out infinite;
  will-change: opacity, transform;
}

.connection-status--online {
  color: #27805d;
}

.connection-status--online .connection-dot {
  background: #43af77;
  box-shadow: 0 0 0 4px rgba(67, 175, 119, 0.13);
}

.connection-status--offline {
  color: var(--orange-dark);
}

.connection-status--offline .connection-dot {
  background: var(--orange);
  box-shadow: 0 0 0 4px rgba(233, 86, 53, 0.14);
}

.clock-status {
  gap: 7px;
  color: var(--muted);
  font-size: 12px;
  font-weight: 750;
}

@keyframes connection-flash {
  0%,
  100% {
    opacity: 0.4;
    transform: scale(0.9);
  }
  50% {
    opacity: 1;
    transform: scale(1.15);
  }
}

.kiosk-main {
  flex: 1;
  width: calc(100% - 96px);
  max-width: 1370px;
  margin: 0 auto;
  padding: 23px 0 36px;
}

.kiosk-main--home {
  padding-top: 34px;
}

.kiosk-content {
  width: 100%;
}

.page-crumb {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 24px;
  color: var(--muted);
  font-size: 12px;
  font-weight: 700;
}

.page-crumb button {
  gap: 7px;
  padding: 6px 0;
  color: var(--ink);
  font-weight: 800;
}

.page-crumb button:hover,
.page-crumb button:focus-visible {
  color: var(--orange);
  outline: none;
}

.crumb-slash {
  color: #b4b8b2;
}

.kiosk-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: calc(100% - 96px);
  min-height: 58px;
  margin: 0 auto;
  border-top: 1px solid rgba(30, 41, 42, 0.1);
  color: var(--muted);
  font-size: 11px;
}

.footer-help,
.footer-help-icon {
  display: flex;
  align-items: center;
}

.footer-help {
  gap: 8px;
}

.footer-help strong {
  color: var(--ink);
}

.footer-help-icon {
  justify-content: center;
  width: 27px;
  height: 27px;
  border-radius: 9px;
  background: var(--accent-soft);
  color: var(--accent);
}

.footer-note {
  letter-spacing: 0.01em;
}

button:focus-visible,
input:focus-visible,
select:focus-visible {
  outline: 3px solid rgba(77, 86, 84, 0.24);
  outline-offset: 2px;
}

@media (max-width: 900px) {
  .kiosk-header,
  .kiosk-main,
  .kiosk-footer {
    width: calc(100% - 44px);
  }

  .kiosk-header {
    min-height: 72px;
  }

  .footer-note {
    display: none;
  }
}

@media (max-width: 620px) {
  .kiosk-header,
  .kiosk-main,
  .kiosk-footer {
    width: calc(100% - 32px);
  }

  .connection-status > span:last-child {
    display: none;
  }

  .header-actions {
    gap: 10px;
  }

  .kiosk-footer {
    min-height: 50px;
  }
}

@media (orientation: landscape) and (min-width: 768px) and (max-height: 820px) {
  .kiosk-header {
    min-height: 66px;
  }

  .kiosk-main {
    padding-top: 14px;
    padding-bottom: 14px;
  }

  .kiosk-main--home {
    padding-top: 18px;
  }

  .page-crumb {
    margin-bottom: 12px;
  }

  .kiosk-footer {
    min-height: 54px;
  }
}
</style>
